import { cva } from 'class-variance-authority';

import { cn } from '@natu/utils/cn';

import {
  type BlokItem,
  DynamicRender,
  sbEditable,
  type SBProps,
  type StoryblokPalette,
} from '../../../../utils';

type ShapeDirection = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

interface SBQuaterCircleWithTextProps {
  colorMobile?: StoryblokPalette;
  colorTablet?: StoryblokPalette;
  colorDesktop?: StoryblokPalette;
  directionMobile?: ShapeDirection;
  directionTablet?: ShapeDirection;
  directionDesktop?: ShapeDirection;
  body?: BlokItem[];
}

const quaterCircleVariants = cva('flex aspect-square h-full w-full min-w-[100px]', {
  variants: {
    directionMobile: {
      'top-left': 'items-end justify-end rounded-[999px_0_0_0] text-right',
      'top-right': 'items-end justify-start rounded-[0_999px_0_0] text-left',
      'bottom-left': 'items-start justify-end rounded-[0_0_0_999px] text-right',
      'bottom-right': 'items-start justify-start rounded-[0_0_999px_0] text-left',
    },
    directionTablet: {
      'top-left': 'md:items-end md:justify-end md:rounded-[999px_0_0_0] md:text-right',
      'top-right': 'md:items-end md:justify-start md:rounded-[0_999px_0_0] md:text-left',
      'bottom-left': 'md:items-start md:justify-end md:rounded-[0_0_0_999px] md:text-right',
      'bottom-right': 'md:items-start md:justify-start md:rounded-[0_0_999px_0] md:text-left',
    },
    directionDesktop: {
      'top-left': 'lg:items-end lg:justify-end lg:rounded-[999px_0_0_0] lg:text-right',
      'top-right': 'lg:items-end lg:justify-start lg:rounded-[0_999px_0_0] lg:text-left',
      'bottom-left': 'lg:items-start lg:justify-end lg:rounded-[0_0_0_999px] lg:text-right',
      'bottom-right': 'lg:items-start lg:justify-start lg:rounded-[0_0_999px_0] lg:text-left',
    },
    color: {
      mobile: `bg-[var(--bg-sb-color-sm)]`,
      tablet: `md:bg-[var(--bg-sb-color-md)]`,
      desktop: `lg:bg-[var(--bg-sb-color-lg)]`,
    },
  },
});

export const SBQuaterCircleWithText = ({ blok }: SBProps<SBQuaterCircleWithTextProps>) => {
  const {
    colorMobile,
    colorDesktop,
    colorTablet,
    directionMobile = 'top-left',
    directionTablet,
    directionDesktop,
    body,
  } = blok;

  const colorStyleSm = colorMobile?.value ? { '--bg-sb-color-sm': colorMobile.value } : undefined;
  const colorStyleMd = colorTablet?.value ? { '--bg-sb-color-md': colorTablet.value } : undefined;
  const colorStyleLg = colorDesktop?.value ? { '--bg-sb-color-lg': colorDesktop.value } : undefined;

  const className = cn(
    quaterCircleVariants({
      directionMobile,
      directionDesktop,
      directionTablet,
    }),
    colorMobile?.value && quaterCircleVariants({ color: 'mobile' }),
    colorTablet?.value && quaterCircleVariants({ color: 'tablet' }),
    colorDesktop?.value && quaterCircleVariants({ color: 'desktop' }),
  );

  const style = {
    ...colorStyleSm,
    ...colorStyleMd,
    ...colorStyleLg,
  } as React.CSSProperties;

  return (
    <div style={style} className={className} {...sbEditable(blok)}>
      <DynamicRender data={body} />
    </div>
  );
};
